import { useCallback, useState } from 'react';
import type { BlacklistEntryDto } from '../../api/cabinetTypes';
import { useAppApi } from '../../app/ApiProvider';
import { formatDateTimeInTz } from '../../domain/datetime';
import { useAsync } from '../../hooks/useAsync';
import { useI18n } from '../../i18n/I18nProvider';
import { BottomSheet } from '../../ui/BottomSheet';
import { CtaButton, SmallButton } from '../../ui/Cta';
import { EmptyState, ErrorState, Loader } from '../../ui/feedback';
import { Screen, ScrollArea, SectionTitle, TopBar } from '../../ui/Screen';
import { Note, Row } from './settings/parts';
import { useBusiness } from './useBusiness';

/**
 * «Ограничения клиентов» кабинета (ТЗ §19, №23).
 *
 * Локальный бан наступает автоматически со второй неявки, и до этого экрана снять его было
 * нечем: оставалось ждать, пока истечёт срок, или снимать саму отметку неявки — а это
 * переписывает историю и меняет глобальный счёт. Здесь уходит только ограничение ЭТОГО
 * заведения: отметки остаются, глобальный бан не затрагивается.
 *
 * ПРОЩЕНИЕ РАЗОВОЕ, А НЕ ИНДУЛЬГЕНЦИЯ: счётчик неявок никуда не делся, и следующая неявка
 * у этого же заведения забанит снова. Об этом сказано текстом (`blNote`), иначе владелец
 * решит, что «снял навсегда».
 *
 * Имя клиента — то же, что в календаре. Ни телефона, ни `tg_id` здесь нет и быть не может
 * (ТЗ §9.3): заведение действует по идентификатору строки, который о человеке не говорит
 * ничего.
 */
export function BlacklistScreen(props: { businessId: string; onBack: () => void }): JSX.Element {
  const { t, lang } = useI18n();
  const api = useAppApi();
  const { businessId } = props;

  const { business } = useBusiness(businessId);
  const list = useAsync(
    useCallback(() => api.cabinet.listBlacklist(businessId), [api, businessId]),
    [businessId],
  );

  const [liftingId, setLiftingId] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);

  const lift = async (entryId: string): Promise<void> => {
    if (liftingId) return;
    setLiftingId(entryId);
    setFailure(null);
    try {
      await api.cabinet.liftBlacklistEntry(businessId, entryId);
      list.reload();
    } catch (err) {
      // Отказ показывается ключом: «только на подписке», «уже не действует» и прочие
      // приходят с сервера и переводятся на языке интерфейса (ТЗ §19, №10).
      setFailure(err);
    } finally {
      setLiftingId(null);
    }
  };

  // Пока организация грузится, пояс неизвестен. Даты в этом случае просто ещё не
  // отрисованы: список грузится параллельно и показывается после него.
  const tz = business?.tz ?? null;

  return (
    <Screen>
      <TopBar title={t('blTitle')} onBack={props.onBack} />

      <ScrollArea>
        <div className="px-4 pt-4">
          <Note>{t('blNote')}</Note>
        </div>

        {list.state.status === 'loading' ? <Loader /> : null}
        {list.state.status === 'failed' ? (
          <ErrorState error={list.state.error} onRetry={list.reload} />
        ) : null}

        {list.state.status === 'ready' ? (
          list.state.data.length === 0 ? (
            <div className="px-4 pt-4">
              <EmptyState>{t('blEmpty')}</EmptyState>
            </div>
          ) : (
            <div className="px-4 pt-5">
              <SectionTitle>{t('blSection')}</SectionTitle>
              {list.state.data.map((entry) => (
                <EntryRow
                  key={entry.id}
                  entry={entry}
                  tz={tz}
                  lang={lang}
                  busy={liftingId === entry.id}
                  onLift={() => void lift(entry.id)}
                />
              ))}
            </div>
          )
        ) : null}
      </ScrollArea>

      <BottomSheet open={failure !== null} title={t('errTitle')} onClose={() => setFailure(null)}>
        <ErrorState error={failure} withTitle={false} />
        <CtaButton variant="ghost" onClick={() => setFailure(null)}>
          {t('close')}
        </CtaButton>
      </BottomSheet>
    </Screen>
  );
}

function EntryRow(props: {
  entry: BlacklistEntryDto;
  tz: string | null;
  lang: 'ru' | 'uz';
  busy: boolean;
  onLift: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const { entry } = props;

  return (
    <Row>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-semibold text-ink-1">{entry.clientName}</div>
        <div className="mt-0.5 text-[13px] text-ink-3">
          {entry.expiresAt === null
            ? t('blForever')
            : props.tz === null
              ? ''
              : t('blUntil', {
                  date: formatDateTimeInTz(entry.expiresAt, props.tz, props.lang),
                })}
        </div>
      </div>
      <SmallButton onClick={props.onLift} disabled={props.busy}>
        {t('blLift')}
      </SmallButton>
    </Row>
  );
}
